'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useConnectorStore } from '@/lib/connectors/store';
import type { ConnectorOperation } from '@/lib/connectors/types';
import {
  Plus,
  Search,
  MoreVertical,
  Trash2,
  GripVertical,
} from 'lucide-react';

interface OperationListProps {
  connectorId: string;
  operations: ConnectorOperation[];
  selectedId?: string;
  onSelect: (operation: ConnectorOperation) => void;
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-green-500/10 text-green-600 border-green-500/20',
  POST: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  PUT: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  PATCH: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  DELETE: 'bg-red-500/10 text-red-600 border-red-500/20',
};

export function OperationList({ connectorId, operations, selectedId, onSelect }: OperationListProps) {
  const { createOperation, deleteOperation } = useConnectorStore();
  const [search, setSearch] = useState('');

  const filteredOperations = operations.filter(
    (op) =>
      op.name.toLowerCase().includes(search.toLowerCase()) ||
      op.path.toLowerCase().includes(search.toLowerCase())
  );

  const handleAddOperation = async () => {
    if (!connectorId) return;

    try {
      const newOperation = await createOperation(connectorId, {
        operation_id: `op_${Date.now()}`,
        name: 'New Operation',
        method: 'GET',
        path: '/new-endpoint',
      });
      onSelect(newOperation);
    } catch (error) {
      console.error('Failed to create operation:', error);
    }
  };

  const handleDeleteOperation = async (op: ConnectorOperation) => {
    if (confirm(`Delete operation "${op.name}"?`)) {
      try {
        await deleteOperation(connectorId, op.id);
      } catch (error) {
        console.error('Failed to delete operation:', error);
      }
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Operations</h3>
          <Button size="sm" variant="ghost" onClick={handleAddOperation} disabled={!connectorId}>
            <Plus className="size-4" />
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8"
          />
        </div>
      </div>

      {/* Operations */}
      <div className="flex-1 overflow-y-auto">
        {!connectorId ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            <p>Save the connector first to add operations</p>
          </div>
        ) : filteredOperations.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {operations.length === 0 ? (
              <>
                <p>No operations yet</p>
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleAddOperation}
                  className="mt-1"
                >
                  Add your first operation
                </Button>
              </>
            ) : (
              <p>No matching operations</p>
            )}
          </div>
        ) : (
          <div className="divide-y">
            {filteredOperations.map((op) => (
              <div
                key={op.id}
                className={`group flex items-center gap-2 p-3 cursor-pointer hover:bg-muted/50 transition-colors ${
                  selectedId === op.id ? 'bg-muted' : ''
                }`}
                onClick={() => onSelect(op)}
              >
                <GripVertical className="size-4 text-muted-foreground opacity-0 group-hover:opacity-100 cursor-grab" />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`font-mono text-xs px-1.5 ${METHOD_COLORS[op.method] || ''}`}
                    >
                      {op.method}
                    </Badge>
                    <span className="font-medium text-sm truncate">{op.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono truncate mt-0.5">
                    {op.path}
                  </p>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6 opacity-0 group-hover:opacity-100"
                    >
                      <MoreVertical className="size-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => handleDeleteOperation(op)}
                    >
                      <Trash2 className="size-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t bg-muted/30">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={handleAddOperation}
          disabled={!connectorId}
        >
          <Plus className="size-4 mr-2" />
          Add Operation
        </Button>
      </div>
    </div>
  );
}
